import { useEffect, useRef, useState } from 'react';
import { Loader2, MessageSquare, Send } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { chatAuraProject } from '@/lib/auraApi';
import { usePermissions } from '@/contexts/PermissionsContext';

const STARTERS = [
  'What is the source SID and target S/4 version?',
  'What does the report say about Material Ledger?',
  'What are the main conversion blockers?',
];

export default function AuraProjectChat({ projectId }) {
  const { hasPermission } = usePermissions();
  const canUpdate = hasPermission('transformations', 'update');
  const [question, setQuestion] = useState('');
  const [messages, setMessages] = useState([]);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const listRef = useRef(null);
  const sendingRef = useRef(false);

  useEffect(() => {
    const el = listRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, sending]);

  const send = async (text) => {
    if (!canUpdate) return;
    const q = String(text || '').trim();
    if (!q) {
      setError('Type a question first.');
      return;
    }
    if (!projectId) {
      setError('This project is missing an id, so chat cannot send.');
      return;
    }
    if (sendingRef.current) return;
    sendingRef.current = true;
    setError('');
    setQuestion('');
    const history = messages
      .filter((m) => m.role === 'user' || m.role === 'assistant')
      .map((m) => ({ role: m.role, content: m.content }));
    setMessages((prev) => [...prev, { role: 'user', content: q }]);
    setSending(true);
    try {
      const data = await chatAuraProject(projectId, q, history);
      setMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          content: data.answer || 'No answer returned.',
          sources: Array.isArray(data.sources) ? data.sources : [],
          retrieval: data.retrieval || '',
        },
      ]);
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || 'Chat failed';
      setError(String(detail));
      setMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          content: `I could not answer that: ${detail}`,
          error: true,
        },
      ]);
    } finally {
      sendingRef.current = false;
      setSending(false);
    }
  };

  return (
    <Card data-testid="aura-project-chat">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <MessageSquare className="h-4 w-4" />
          Ask about this landscape
        </CardTitle>
        <CardDescription>
          Answers come from this project’s inventory, summary, and readiness extract — not from
          other customers.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex items-center gap-2" data-testid="aura-chat-bar">
          <Input
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                send(question);
              }
            }}
            placeholder={
              canUpdate
                ? 'Ask a question about SID, partners, blockers, sizing…'
                : 'Chat requires Transformations update permission'
            }
            disabled={sending || !canUpdate}
            maxLength={4000}
            className="min-w-0 flex-1"
            data-testid="aura-chat-input"
          />
          <Button
            type="button"
            onClick={() => send(question)}
            disabled={sending || !canUpdate || !question.trim()}
            className="shrink-0"
            data-testid="aura-chat-send"
          >
            {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            Send
          </Button>
        </div>

        {error ? (
          <p className="text-xs text-red-300" data-testid="aura-chat-error">
            {error}
          </p>
        ) : null}

        <div
          ref={listRef}
          className="max-h-80 space-y-3 overflow-y-auto rounded-lg border border-[var(--line)] bg-[var(--inset)] p-3"
          data-testid="aura-chat-thread"
        >
          {messages.length === 0 && !sending ? (
            <div className="space-y-2">
              {canUpdate ? (
                <>
                  <p className="text-xs text-muted-foreground">Try one of these:</p>
                  <div className="flex flex-wrap gap-2">
                    {STARTERS.map((hint) => (
                      <button
                        key={hint}
                        type="button"
                        className="rounded-full border border-[var(--line)] px-2.5 py-1 text-left text-xs text-muted-foreground hover:border-sky-500/40 hover:text-sky-200"
                        onClick={() => send(hint)}
                      >
                        {hint}
                      </button>
                    ))}
                  </div>
                </>
              ) : (
                <p className="text-xs text-muted-foreground">
                  View-only: chat requires Transformations update permission.
                </p>
              )}
            </div>
          ) : (
            messages.map((m, i) => (
              <div
                key={`${m.role}-${i}`}
                className={m.role === 'user' ? 'ml-8 text-right' : 'mr-8'}
                data-testid={`aura-chat-msg-${m.role}`}
              >
                <div
                  className={`inline-block max-w-full whitespace-pre-wrap rounded-lg px-3 py-2 text-sm ${
                    m.role === 'user'
                      ? 'bg-sky-500/15 text-sky-100'
                      : m.error
                        ? 'bg-red-500/10 text-red-100'
                        : 'bg-black/20 text-[var(--text)]'
                  }`}
                >
                  {m.content}
                </div>
                {m.sources?.length ? (
                  <p className="mt-1 text-[11px] text-muted-foreground">
                    Sources: {m.sources.join(', ')}
                  </p>
                ) : null}
              </div>
            ))
          )}
          {sending ? (
            <p className="flex items-center gap-2 text-xs text-muted-foreground">
              <Loader2 className="h-3 w-3 animate-spin" />
              Reading this project’s analysis…
            </p>
          ) : null}
        </div>
      </CardContent>
    </Card>
  );
}
